<template>
  <div class="mission-asset-table" ref="container">
    <v-toolbar flat ref="toolbar">
      <!-- <v-toolbar-title class="flex-grow-1"> -->
      <div class="flex-grow-1">
        <div class="d-flex justify-space-between" v-if="hasTableTitle">
          <div>{{ dateStart }}</div>
          <div v-if="dateStart !== dateEnd">-</div>
          <div v-if="dateStart !== dateEnd">
            {{ dateEnd }}
          </div>
        </div>
      </div>
      <!-- </v-toolbar-title> -->
      <v-spacer />
      {{ table.pagination.total || 0 }} ASSETS
      <v-spacer />
      <mission-asset-table-export-button :params="tableParams" />
      <mission-asset-table-filter @filter="onFilter" ref="filter">
        <template #activator="{ on }">
          <v-btn light class="mx-1" v-on="on"> FILTER </v-btn>
        </template>
      </mission-asset-table-filter>
    </v-toolbar>
    <base-table
      :selected.sync="missionAssetsSelected"
      :fetch-service="$options.missionAssetService"
      fetch-func="all"
      :fetch-params="{ ...tableParams, include: 'mission,comments,newAssetLog' }"
      :limit=200
      :fetch-on-init="false"
      :headers="table.headers"
      :height="table.height"
      :pagination.sync="table.pagination"
      :props="{
        'single-select': true,
      }"
      @fetch="onTableFetch"
      ref="baseTable"
    >
      <template #item.assetId="{ item }">
        <span v-if="item.thirdPartyCode">{{ item.thirdPartyCode }}</span>
        <v-tooltip v-else top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ item.id }}
              <v-icon color="orange">mdi-alert-circle</v-icon>
            </span>
          </template>
          External asset data unavailable
        </v-tooltip>
        <v-tooltip v-if="getMissionAssetComment(item)" top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="ml-2">
              <v-icon>mdi-message-reply</v-icon>
            </span>
          </template>
          {{ getMissionAssetComment(item).content }}
        </v-tooltip>
      </template>
      <template #item.type> CATCHPIT </template>
      <template #item.lastService="{ item }">
        <span class="text-no-wrap">
          <v-btn small @click="navigateToMission(item.mission)">{{ item.createdAt | dateLocal("DD-MMM-YYYY") | toUpperCase }}</v-btn>
        </span>
      </template>
      <template #item.newAsset="{ item }">
        <span class="text-no-wrap">
          {{ item.newAsset ? 'YES' : 'NO' }}
        </span>
      </template>
      <template #item.success="{ item }">
        {{ item.passFail === "pass" ? "YES" : "NO" }}
      </template>
      <template #item.fault="{ item }">
        {{ item.isFault ? "YES" : "NO" }}
      </template>
    </base-table>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { formatDateLocal } from "@/helpers/dates";
import { MissionAssetService } from "@/services/missionAsset.service";
import BaseTable from "@/components/Base/BaseTable";
import MissionAssetTableExportButton from "@/components/MissionAsset/MissionAssetTableExportButton";
import MissionAssetTableFilter from "@/components/MissionAsset/MissionAssetTableFilter";

export default {
  missionAssetService: MissionAssetService,
  components: {
    BaseTable,
    MissionAssetTableExportButton,
    MissionAssetTableFilter,
  },
  data: () => ({
    filters: {},
    table: {
      headers: [
        {
          text: "Asset",
          value: "assetId",
        },
        {
          text: "Date",
          value: "lastService",
        },
        {
          text: "New",
          value: "newAsset",
        },
        {
          text: "Cleaned",
          value: "success",
          terminology: "mission_assets.pass_fail",
          slot: true,
        },
        {
          text: "Fault/s",
          value: "fault",
        },
      ],
      height: 300,
      pagination: {
        currentPage: 1,
      },
    },
    missionAssetsSelected: [],
  }),
  computed: {
    ...mapGetters("config", ["getConfigByKey", "getTerminology"]),
    tableParams() {
      return {
        group_by: 'core_id',
        order_by: '-created_at',
        mission_id: this.filters.mission || undefined,
        assets_search: this.filters.assets_search || undefined,
        region: this.filters.region || undefined,
        assessment_period_start: this.filters.dateStart
          ? formatDateLocal(
              moment(this.filters.dateStart).startOf("day"),
              ""
            )
          : undefined,
        assessment_period_end: this.filters.dateEnd
          ? formatDateLocal(
              moment(this.filters.dateEnd).endOf("day"),
              ""
            )
          : undefined,
        is_cleaned: this.filters.serviceSuccessful ? 1 : undefined,
        is_failed: this.filters.serviceFailed ? 1 : undefined,
        faults: this.filters.faults ? this.filters.faults.slice(0) : [],
        is_new_asset: this.filters.newAsset ? 1 : undefined,
        service_type: this.filters.serviceType || undefined,
      };
    },
    hasTableTitle() {
      return (
        this.filters.serviceType !== "serviceOverdue" &&
        (this.filters.dateStart || this.filters.dateEnd)
      );
    },
    dateStart() {
      const dateStart = this.filters.dateStart;
      return dateStart ? moment(dateStart).format("DD-MMM-YYYY") : "N/A";
    },
    dateEnd() {
      const dateEnd = this.filters.dateEnd;
      return dateEnd ? moment(dateEnd).format("DD-MMM-YYYY") : "N/A";
    },
    terminology() {
      let terms = {};
      const terminologies = this.getConfigByKey('terminologies');
      return terminologies;
    }
  },
  mounted() {
    setTimeout(() => {
      this.setTableHeight();
    }, 500);
  },
  watch: {
    missionAssetsSelected(value) {
      this.$emit("select", value);
      this.$emit("update:selected", value);
    },
    terminology(terminology) {
      // Update table headers that rely on terminology
      let table_headers = this.table.headers;
      table_headers.forEach((header, i) => {
        const lookup = header.terminology;
        if (lookup) {
          table_headers[i].text = this.getTerminology(lookup) || header.text;
        }
      });
      this.$set(this.$data.table, 'headers', table_headers);
    }
  },
  methods: {
    setTableHeight() {
      const containerHeight = this.$refs.container.clientHeight;
      const toolbarHeight = this.$refs.toolbar.$el.clientHeight;
      this.table.height = containerHeight - toolbarHeight;
    },
    onFilter(filters) {
      this.filters = filters;
      this.$set(this.$data.table.pagination, 'currentPage', 1);
      this.$nextTick(() => {
        this.$refs.baseTable.fetchItems();
      });
      this.$emit("filter", filters);
    },
    onTableFetch(items) {
      this.$emit("fetch", items);
    },
    onTableMissionClick(mission) {
      if (!mission) return;
      const filter = this.$refs.filter;
      filter.filters.mission = mission.id;
      this.$nextTick(() => {
        filter.filter();
      });
    },
    navigateToMission(mission) {
      this.$store.commit("missions/SET_SELECTED", mission);
      this.$router.push({ name: "missions" });
    },
    getMissionAssetComment(missionAsset) {
      let comment = null;
      if (missionAsset && missionAsset.comments.length > 0) {
        comment = Object.assign({}, missionAsset.comments.slice(0, 1).shift());
      }
      return comment;
    }
  },
};
</script>

<style lang="scss">
.mission-asset-table {
  height: 100%;
}
</style>
